import { Col, Row, Text } from 'components';
import { theme } from 'lib';
import React from 'react';
import {
  ANCHOR_BOUNDS,
  ANCHOR_EDGE,
  ANCHOR_KIND,
  ANCHOR_KIND_LABEL,
  CLOSE_ANCHOR_KINDS,
  MATCH_EVENT_LABEL,
  MATCH_EVENT_ORDER,
  OPEN_ANCHOR_KINDS,
  anchorSentence
} from '../anchors';
import { resolveAnchorLocally } from '../timeline';
import {
  DateInput,
  FieldError,
  FieldHint,
  NumberInput,
  Select,
  fmtDateTime,
  fmtMaybe,
  localTzLabel
} from './timelineUi';

// ONE EDGE of a wave — when it opens, or when it closes.
//
// The two pickers ARE the schedule. Presets sit above them and only seed them, so there is no
// mode to switch and no shape a wave is locked into: an admin can start from preset A and drag or
// retype either edge into anything the grammar allows. That is the whole reason the four loose
// fields (startMinute / endMinute / startsAt / endsAt) and their hand-enforced combinations are
// gone.
//
// The kind dropdown decides which payload field is live. Everything the admin typed under the
// other kinds stays in the draft so flipping back and forth loses nothing, and nothing dead
// reaches the wire — see `anchorPayload`, which emits only the field the kind selects because the
// API REJECTS a stray one rather than ignoring it.

const KindRow = ({ label, children }) => (
  <Col noFlex wid="100%" gap="0">
    <Text b2 bold marg="0 0 6px" style={{ fontSize: 13 }}>
      {label}
    </Text>
    {children}
  </Col>
);

/**
 * The offset field for a `match_event` anchor. Signed on purpose — "FT −10" (ten minutes before
 * the final whistle) is one of the shapes the client asked for, and a magnitude-plus-direction
 * pair would make it two controls for one number.
 */
const OffsetField = ({ value, onChange, disabled, error, eventLabel }) => (
  <KindRow label="Offset (minutes)">
    <NumberInput
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="0"
      step={1}
      min={-ANCHOR_BOUNDS.MAX_OFFSET_MIN}
      max={ANCHOR_BOUNDS.MAX_OFFSET_MIN}
      disabled={disabled}
      hasError={!!error}
      aria-label="Offset in minutes"
    />
    <FieldHint>
      {`Signed, within ${ANCHOR_BOUNDS.MAX_OFFSET_MIN} minutes either side. Blank or 0 sits exactly on ${eventLabel}; −10 is ten minutes before it.`}
    </FieldHint>
  </KindRow>
);

const AnchorPicker = ({
  edge,
  draft,
  onChange,
  error,
  disabled,
  disabledReason,
  // Resolution context from the timeline response — lets the picker show what the edge currently
  // lands on, and whether that time is a fact or an estimate.
  phaseByEvent,
  postFtMinutes,
  openResolved,
  // `datetime-local` bounds for the ABSOLUTE field — the times this team has already given away,
  // greyed out by the picker itself so an overlap cannot be chosen in the first place.
  min,
  max
}) => {
  const isOpen = edge === ANCHOR_EDGE.OPEN;
  const kinds = isOpen ? OPEN_ANCHOR_KINDS : CLOSE_ANCHOR_KINDS;
  const patch = (fields) => onChange({ ...draft, ...fields });

  const resolved = disabled
    ? null
    : resolveAnchorLocally(
        // The picker resolves the DRAFT, so the readout tracks what the admin is typing rather
        // than what was last saved.
        draft.kind === ANCHOR_KIND.ABSOLUTE
          ? { kind: ANCHOR_KIND.ABSOLUTE, at: draft.at }
          : draft.kind === ANCHOR_KIND.MATCH_MINUTE
            ? { kind: ANCHOR_KIND.MATCH_MINUTE, minute: Number(draft.minute) }
            : draft.kind === ANCHOR_KIND.DURATION
              ? {
                  kind: ANCHOR_KIND.DURATION,
                  durationMin: Number(draft.durationMin),
                  untilEvent: draft.untilEvent || undefined
                }
              : {
                  kind: ANCHOR_KIND.MATCH_EVENT,
                  event: draft.event,
                  offsetMin: Number(draft.offsetMin) || 0
                },
        { phaseByEvent, postFtMinutes, openResolved }
      );

  return (
    <Col
      noFlex
      wid="100%"
      gap="12px"
      pad="14px 15px"
      style={{
        borderRadius: 12,
        border: `1px solid ${theme.GREY_20}`,
        background: disabled ? theme.GREY_10 : '#fff',
        boxSizing: 'border-box'
      }}
    >
      <Row center between noFlex wid="100%" gap="10px">
        <Text b2 bold marg="0" style={{ fontSize: 13.5 }}>
          {isOpen ? 'Opens' : 'Closes'}
        </Text>
        {resolved && (
          <Text
            c1
            col={resolved.observed ? theme.GREY_70 : theme.GREY_60}
            marg="0"
            style={{ fontSize: 11.5, fontStyle: resolved.observed ? 'normal' : 'italic' }}
            title={
              resolved.observed
                ? 'The match reached this moment — this time is recorded.'
                : 'Projected from kick-off. This will move as the match progresses.'
            }
          >
            {`${fmtMaybe(resolved.at, resolved.observed, fmtDateTime)}${
              resolved.observed ? '' : ' · projected'
            }`}
          </Text>
        )}
      </Row>

      <KindRow label="Set by">
        <Select
          value={draft.kind}
          disabled={disabled}
          hasError={!!error}
          onChange={(e) => patch({ kind: e.target.value })}
          aria-label={isOpen ? 'How the wave opens' : 'How the wave closes'}
        >
          {kinds.map((kind) => (
            <option key={kind} value={kind}>
              {ANCHOR_KIND_LABEL[kind]}
            </option>
          ))}
        </Select>
      </KindRow>

      {draft.kind === ANCHOR_KIND.MATCH_EVENT && (
        <>
          <KindRow label="Match event">
            <Select
              value={draft.event}
              disabled={disabled}
              hasError={!!error}
              onChange={(e) => patch({ event: e.target.value })}
              aria-label="Match event"
            >
              {MATCH_EVENT_ORDER.map((event) => (
                <option key={event} value={event}>
                  {MATCH_EVENT_LABEL[event]}
                </option>
              ))}
            </Select>
          </KindRow>
          <OffsetField
            value={draft.offsetMin}
            onChange={(offsetMin) => patch({ offsetMin })}
            disabled={disabled}
            error={error}
            eventLabel={MATCH_EVENT_LABEL[draft.event] || 'the event'}
          />
        </>
      )}

      {draft.kind === ANCHOR_KIND.MATCH_MINUTE && (
        <KindRow label="Match minute">
          <NumberInput
            value={draft.minute}
            onChange={(e) => patch({ minute: e.target.value })}
            placeholder="46"
            step={1}
            min={ANCHOR_BOUNDS.MIN_MATCH_MINUTE}
            max={ANCHOR_BOUNDS.MAX_MATCH_MINUTE}
            disabled={disabled}
            hasError={!!error}
            aria-label="Match minute"
          />
          <FieldHint>
            {`${ANCHOR_BOUNDS.MIN_MATCH_MINUTE}–${ANCHOR_BOUNDS.MAX_MATCH_MINUTE}, read off the live match clock. Coarser than an event — the clock stops for the interval and stretches through stoppage, so a minute never counts as observed.`}
          </FieldHint>
        </KindRow>
      )}

      {draft.kind === ANCHOR_KIND.ABSOLUTE && (
        <KindRow label="Date & time">
          <DateInput
            type="datetime-local"
            value={draft.at}
            onChange={(e) => patch({ at: e.target.value })}
            disabled={disabled}
            hasError={!!error}
            min={min || undefined}
            max={max || undefined}
            aria-label="Date and time"
          />
          <FieldHint>
            {`Ignores the match entirely. Your local time (${localTzLabel}); stored in UTC.` +
              (min || max
                ? ` Limited to ${min || 'any time'} – ${max || 'open-ended'}: the rest of this window belongs to another wave.`
                : '')}
          </FieldHint>
        </KindRow>
      )}

      {draft.kind === ANCHOR_KIND.DURATION && (
        <KindRow label="Minutes after opening">
          <NumberInput
            value={draft.durationMin}
            onChange={(e) => patch({ durationMin: e.target.value })}
            placeholder="20"
            step={1}
            min={ANCHOR_BOUNDS.MIN_DURATION_MIN}
            max={ANCHOR_BOUNDS.MAX_DURATION_MIN}
            disabled={disabled}
            hasError={!!error}
            aria-label="Minutes after the wave opens"
          />
          <FieldHint>
            {`${ANCHOR_BOUNDS.MIN_DURATION_MIN}–${ANCHOR_BOUNDS.MAX_DURATION_MIN}. Measured from when the wave ACTUALLY opens, so it holds its length even if the match runs late.`}
          </FieldHint>
        </KindRow>
      )}

      <FieldError>{error}</FieldError>

      {disabled && disabledReason && <FieldHint>{disabledReason}</FieldHint>}

      {!disabled && !error && (
        <Text c1 col={theme.GREY_50} marg="0" style={{ fontSize: 11 }}>
          {anchorSentence(
            {
              kind: draft.kind,
              event: draft.event,
              offsetMin: draft.offsetMin === '' ? 0 : Number(draft.offsetMin),
              minute: draft.minute === '' ? null : Number(draft.minute),
              at: draft.at || null,
              durationMin: draft.durationMin === '' ? null : Number(draft.durationMin),
              untilEvent: draft.untilEvent || undefined
            },
            fmtDateTime
          )}
        </Text>
      )}
    </Col>
  );
};

export default AnchorPicker;
